import { TASK3_NUDGE_SILENCE_SECONDS } from './task3NudgePolicy';

export const TASK3_AUDIBLE_NUDGE_MS = TASK3_NUDGE_SILENCE_SECONDS * 1000;

/** Do not prefetch during the first 1.5s of silence (avoid noise right after speech). */
export const TASK3_MIN_PREFETCH_SILENCE_MS = 1500;

export const TASK3_DEFAULT_BACKEND_AUDIO_LATENCY_MS = 3500;
export const TASK3_MAX_BACKEND_LATENCY_ESTIMATE_MS = 5000;
export const TASK3_MIN_BACKEND_LATENCY_ESTIMATE_MS = 500;

export function clampBackendLatencyEstimateMs(estimateMs: number): number {
  return Math.min(
    TASK3_MAX_BACKEND_LATENCY_ESTIMATE_MS,
    Math.max(TASK3_MIN_BACKEND_LATENCY_ESTIMATE_MS, Math.round(estimateMs)),
  );
}

/** Silence duration (ms) at which the client may send the nudge prefetch request. */
export function computeNudgePrefetchAtMs(estimatedBackendLatencyMs: number): number {
  const est = clampBackendLatencyEstimateMs(estimatedBackendLatencyMs);
  return Math.max(TASK3_MIN_PREFETCH_SILENCE_MS, TASK3_AUDIBLE_NUDGE_MS - est);
}

export function computeAudibleReleaseAtMs(silenceStartedAtMs: number): number {
  return silenceStartedAtMs + TASK3_AUDIBLE_NUDGE_MS;
}

export function elapsedSilenceMs(nowMs: number, silenceStartedAtMs: number): number {
  return Math.max(0, nowMs - silenceStartedAtMs);
}

export function canStartNudgePrefetch(input: {
  elapsedSilenceMs: number;
  estimatedBackendLatencyMs: number;
  prefetchAlreadyStarted: boolean;
  nudgeAllowanceConsumed: boolean;
  userHasEverSpoken: boolean;
  remainingSeconds: number;
}): boolean {
  if (!input.userHasEverSpoken || input.nudgeAllowanceConsumed || input.prefetchAlreadyStarted) {
    return false;
  }
  if (input.remainingSeconds <= 45) return false;
  const prefetchAt = computeNudgePrefetchAtMs(input.estimatedBackendLatencyMs);
  return input.elapsedSilenceMs >= prefetchAt && input.elapsedSilenceMs < TASK3_AUDIBLE_NUDGE_MS;
}

export function canReleaseHeldNudgeAt(input: {
  nowMs: number;
  audibleReleaseAtMs: number;
  hasHeldAudio: boolean;
}): boolean {
  return input.hasHeldAudio && input.nowMs >= input.audibleReleaseAtMs;
}

/** Slow path: silence reached 6s but prefetch never started (e.g. very late eligibility). */
export function shouldSendImmediateNudgeAtSixSeconds(input: {
  elapsedSilenceMs: number;
  nudgeAllowanceConsumed: boolean;
  prefetchStarted: boolean;
  nudgeRequestInFlight: boolean;
  userHasEverSpoken: boolean;
  remainingSeconds: number;
}): boolean {
  if (!input.userHasEverSpoken || input.nudgeAllowanceConsumed) {
    return false;
  }
  if (input.prefetchStarted || input.nudgeRequestInFlight) {
    return false;
  }
  if (input.remainingSeconds <= 45) return false;
  return input.elapsedSilenceMs >= TASK3_AUDIBLE_NUDGE_MS;
}

export function updateRollingBackendLatencyEstimateMs(
  currentEstimateMs: number,
  sampleMs: number,
): number {
  const blended = Math.round(currentEstimateMs * 0.65 + sampleMs * 0.35);
  return clampBackendLatencyEstimateMs(blended);
}
